import React, { useEffect } from 'react';
import { Button, Empty, Skeleton } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';

import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';

import { DetailsPageHeader } from '../../components/DetailsPageHeader';
import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import { AssessmentDetails } from './AssessmentDetails';
import { MatchDetailStatus } from './MatchDetailStatus';
import { MatcherProcessingDetails } from './MatcherProcessingDetails';
import { MatchingPageLayout } from './matching.styles';
import { CandidateProjectMatchDetail, MatchingSource } from './matching.types';
import { candidateName, matchingListPath, processingReason } from './matchingPresentation';
import { MatchingReadError } from './MatchingReadError';
import { useMatchDetails } from './useMatchDetails';

const UnavailableAssessment = ({ data }: { data: CandidateProjectMatchDetail }) => {
  const anonymized = data.processing.blockedReason === 'candidate-anonymized';
  const failed = [data.processing.quickStatus, data.processing.detailedStatus].some((status) =>
    status?.startsWith('FAILED')
  );
  const message = anonymized
    ? 'Assessment unavailable for an anonymized candidate'
    : data.publishedOutcome === 'QUICK_REJECTED'
    ? 'This pair did not pass the initial matching check'
    : data.processingActive
    ? 'The assessment is being prepared'
    : failed
    ? 'The assessment could not be completed'
    : 'No assessment is available yet';
  const description = anonymized
    ? 'Candidate context is masked and previous assessment content is unavailable.'
    : data.publishedOutcome === 'QUICK_REJECTED'
    ? 'No detailed assessment was published for this pair. You can still open the candidate or project record.'
    : data.processingActive
    ? 'This page updates automatically while this pair is processing.'
    : data.processing.blockedReason
    ? processingReason(data.processing.blockedReason)
    : 'This pair has no published detailed assessment. Refresh this page later to check for one.';
  return (
    <div className="matching-empty">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <>
            <h2>{message}</h2>
            <p className="matching-muted">{description}</p>
          </>
        }
      />
    </div>
  );
};

export const MatchDetailsPage = ({ sourceType }: { sourceType: MatchingSource['type'] }) => {
  const { id = '', matchId = '' } = useParams();
  const source: MatchingSource = { type: sourceType, uuid: id };
  const { search } = useLocation();
  const backPath = `${matchingListPath(source)}${search}`;
  const query = useMatchDetails(sourceType === 'candidate' ? id : matchId, sourceType === 'project' ? id : matchId);
  const { data } = query;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id, matchId, sourceType]);
  return (
    <MatchingPageLayout>
      <Link className="matching-back" to={backPath}>
        <ArrowLeftOutlined /> Back to matches
      </Link>
      <div className="matching-detail-header">
        <DetailsPageHeader
          titleLevel={1}
          title={
            data
              ? sourceType === 'project'
                ? candidateName(data.candidate)
                : data.project.name || 'Unnamed project'
              : 'Match assessment'
          }
          actions={
            // Reload re-reads the stored pair; it never re-runs the assessment.
            !query.isError && (
              <Button
                icon={<ReloadOutlined />}
                loading={query.isFetching && !query.isLoading}
                onClick={() => query.refetch()}
              >
                Reload
              </Button>
            )
          }
        />
      </div>
      {data && (
        <>
          <div className="matching-muted">
            {sourceType === 'project'
              ? `Match for ${data.project.name || 'Unnamed project'}`
              : `Match for ${candidateName(data.candidate)}`}
            {!data.project.active ? ' · Inactive project' : ''}
          </div>
          {data.candidate.appliedPosition && <div className="matching-secondary">{data.candidate.appliedPosition}</div>}
          <div className="matching-detail-context">
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to={pathToUrl(PageRoutesEnum.candidateEdit, { id: data.candidate.uuid })}
            >
              Open candidate ↗
            </Link>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to={pathToUrl(PageRoutesEnum.projectsEdit, { id: data.project.uuid })}
            >
              Open project ↗
            </Link>
            {data.existingRecruitment ? (
              <Link
                target="_blank"
                rel="noopener noreferrer"
                to={pathToUrl(PageRoutesEnum.recruitmentEdit, { id: data.existingRecruitment.uuid })}
              >
                Recruitment: {data.existingRecruitment.status.name} ↗
              </Link>
            ) : (
              <span className="matching-muted">No existing recruitment</span>
            )}
          </div>
        </>
      )}
      <div style={{ marginTop: 24 }}>
        <MatchingReadError
          error={query.error}
          detail
          retained={!!data}
          onRetry={() => query.refetch()}
          backTo={backPath}
        />
        {query.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {data && (
          <>
            <MatchDetailStatus data={data} />
            {data.assessment ? (
              <AssessmentDetails assessment={data.assessment} />
            ) : (
              <UnavailableAssessment data={data} />
            )}
            <MatcherProcessingDetails data={data} />
          </>
        )}
      </div>
    </MatchingPageLayout>
  );
};
